import { useEffect } from 'react'
import { Alert } from '@/ui'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { findEstimateCoverage } from '@/features/taxes/quarterly/estimateCoverage'
import { ProductFeatureId } from '@/actions/permissionActions'
import {
  selectFirstErrorMessageForKeys,
  selectIsFetchingForKeys,
} from '@/reducers/fetch'
import {
  FETCH_ALL_QUARTERLY_TAX_ESTIMATION_DETAILS_KEY,
  fetchAllQuarterlyTaxEstimateDetailsIfNeeded,
} from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import {
  selectActiveQuarterlyTaxEstimateDetails,
  selectQuarterlyTaxEstimateDetailsByYear,
} from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
import {
  FETCH_USER_TAX_ESTIMATES_KEY,
  fetchSingleUserTaxEstimatesIfNeeded,
  UserTaxEstimate,
} from '@/features/taxes/quarterly/userTaxEstimates.slice'
import { selectUserTaxEstimates } from '@/features/taxes/quarterly/userTaxEstimates.selector'
import {
  getFinancialProfileByUserId,
  getUserById,
} from '@/selectors/user.selectors'

const findCoverageGap = ({
  estimates,
  taxYear,
  expectedPerType,
  homeState,
}: {
  estimates: UserTaxEstimate[]
  taxYear: string
  expectedPerType: number
  homeState: string | null | undefined
}) => {
  if (expectedPerType === 0) return null

  const { missingFederal, missingHomeState, homeStateUnusable } =
    findEstimateCoverage({ estimates, taxYear, expectedPerType, homeState })

  const missing = [
    missingFederal ? 'federal' : null,
    missingHomeState ? 'home-state' : null,
  ].filter((kind): kind is string => kind !== null)

  if (missing.length) {
    return {
      variant: 'error' as const,
      title: `Missing ${missing.join(' and ')} estimates for ${taxYear}`,
      description: homeStateUnusable
        ? 'Without these rows the customer cannot enter payments or receive an estimate. This account also has no home state we recognise, so the home-state rows cannot be generated until that is corrected on the tax profile.'
        : 'Without these rows the customer cannot enter payments or receive an estimate. The next calculation run creates them, or you can trigger it now by saving the tax profile.',
    }
  }

  if (homeStateUnusable) {
    return {
      variant: 'warning' as const,
      title: `No home state we recognise, so ${taxYear} holds federal estimates only`,
      description:
        'Home state is free text, and a value like "Unsure" cannot be priced. Setting a real state on the tax profile generates the missing home-state estimates.',
    }
  }

  return null
}

/**
 * An empty estimates table cannot explain itself, and a missing home state is
 * the usual reason for one. Read-only: it reports, it does not repair.
 */
export const QuarterlyTaxEstimateCoverageAlert = ({
  userId,
}: {
  userId: number
}) => {
  const dispatch = useAppDispatch()
  const activeQuarter = useReselector(selectActiveQuarterlyTaxEstimateDetails)
  const taxYear = activeQuarter?.taxYear
  const detailsForYear = useReselector(
    selectQuarterlyTaxEstimateDetailsByYear,
    taxYear ?? ''
  )
  const estimates = useReselector(selectUserTaxEstimates, userId)
  const financialProfile = useReselector(getFinancialProfileByUserId, userId)
  const user = useReselector(getUserById, userId)
  const hasQuarterlyTaxesEntitlement = user?.featurePermissions?.some(
    (permission) => permission.id === ProductFeatureId.quarterlyTaxes
  )

  const fetchKeys = [
    FETCH_ALL_QUARTERLY_TAX_ESTIMATION_DETAILS_KEY,
    FETCH_USER_TAX_ESTIMATES_KEY(userId),
  ]
  const isFetching = useReselector(selectIsFetchingForKeys, fetchKeys)
  const errorMessage = useReselector(selectFirstErrorMessageForKeys, fetchKeys)

  useEffect(() => {
    dispatch(fetchAllQuarterlyTaxEstimateDetailsIfNeeded())
    dispatch(fetchSingleUserTaxEstimatesIfNeeded(userId))
  }, [dispatch, userId])

  if (isFetching) return null

  // An empty estimate list means "missing" only if we know the list is real.
  if (errorMessage) {
    return (
      <Alert
        variant="warning"
        role="status"
        title="Could not check tax estimate coverage"
        description={errorMessage}
      />
    )
  }

  // No active quarter says nothing about the account.
  if (!taxYear) return null

  // Without the entitlement, an empty table is correct, not a gap.
  if (!hasQuarterlyTaxesEntitlement) return null

  const finding = findCoverageGap({
    estimates,
    taxYear,
    expectedPerType: Object.keys(detailsForYear).length,
    homeState: financialProfile?.homeState,
  })
  if (!finding) return null

  return (
    <Alert
      variant={finding.variant}
      role="status"
      title={finding.title}
      description={finding.description}
    />
  )
}
